// Workflow Hygiene Issue-Type Compliance card. Headline `compliancePercent`
// in tier colour, "target 100%" hint, non-canonical types tag list, and a
// canonical reminder populated from the API. Empty state when notConfigured.

import { Badge, Card, CardContent } from '@/components/ui';
import type { IIssueTypeComplianceSection, IMetricThresholdSet } from '@skystone/shared';
import { useMetricDefinition } from '@/hooks/use-metric-definition';
import { getTierColorClass } from '@/lib/metric-color-class';
import { cn } from '@/lib/utils';
import { useOpenDrill } from './drill/use-open-drill';

interface IssueTypeComplianceCardProps {
  data: IIssueTypeComplianceSection | { notConfigured: true };
}

/**
 * Issue-Type Compliance is `higher_better`, so the success tier is the one
 * with `upperBound === null` (i.e. +∞). Its `lowerBound` is the threshold
 * (100 by default — admin-editable via PR 4). Returns `null` if the set is
 * missing or no lowerBound is configured; caller hides the hint then.
 */
function higherBetterTargetLowerBound(set: IMetricThresholdSet | null): number | null {
  if (!set) return null;
  const successTier = set.tiers.find((t) => t.colorToken === 'success');
  return successTier?.lowerBound ?? null;
}

export function IssueTypeComplianceCard({ data }: Readonly<IssueTypeComplianceCardProps>) {
  const definition = useMetricDefinition('issueTypeCompliance');
  const openDrill = useOpenDrill();
  const thresholdSet: IMetricThresholdSet | null = definition
    ? { metricKey: definition.metricKey, direction: definition.direction, tiers: definition.tiers }
    : null;
  const targetMin = higherBetterTargetLowerBound(thresholdSet);

  if (data.notConfigured) {
    return (
      <Card className="h-full shadow-none">
        <CardContent className="space-y-2 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Issue-Type Compliance
          </p>
          <p className="text-sm italic text-muted-foreground">
            Standards not yet configured
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full shadow-none">
      <CardContent className="space-y-3 p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Issue-Type Compliance
        </p>
        <div className="flex items-baseline gap-2">
          <span
            className={cn('text-3xl font-semibold tabular-nums', getTierColorClass(data.compliancePercent, thresholdSet))}
          >
            {data.compliancePercent}%
          </span>
          {targetMin !== null && (
            <span className="text-xs text-muted-foreground">target {targetMin}%</span>
          )}
        </div>

        {data.nonCanonical.length > 0 && (
          <div className="flex flex-wrap gap-1.5" aria-label="Non-canonical issue types">
            {data.nonCanonical.map(({ type, itemCount }) => (
              <Badge key={type} variant="secondary" className="font-normal">
                {type} · {itemCount}
              </Badge>
            ))}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">
          Canonical: {data.canonical.join(' · ')}
        </p>

        <button
          type="button"
          onClick={() => openDrill('types')}
          className="block text-left text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          View {(data.inScopeCount - data.compliantCount).toLocaleString()} items →
        </button>
      </CardContent>
    </Card>
  );
}
